import { useCallback, useEffect, useState } from "react";
import { visibleFields } from "../fields";
import { ApiError, fetchCoverage, type CoverageEntry, type CoverageResponse } from "../engine/useRun";
import { Banner, Button, Card, Chip, ErrorState, Skeleton } from "./primitives";
import { Icon } from "./Icon";
import { Link, type Route } from "./router";

/**
 * Coverage. The whole point of this page is the NEGATIVE space.
 *
 * A legal tool that is 80% ingested is worse than useless, because the student
 * can't tell which 20% is missing. So what we DON'T hold is listed as loudly as
 * what we do — same size, same weight, top of the page.
 */

export function CoverageScreen({ navigate }: { navigate: (r: Route) => void }) {
  const [data, setData] = useState<CoverageResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  // Whichever pack answers from a corpus is the one this page is about — found
  // by its declared `retrieval`, never by hardcoding the id "law".
  const corpusField = visibleFields().find((f) => f.retrieval);

  const load = useCallback(async () => {
    setError(null);
    setData(null);
    try {
      setData(await fetchCoverage());
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError("صار خطأ", 500, "server"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error.message} onRetry={() => void load()} />;

  if (!data) {
    return (
      <div className="grid gap-3">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-20 w-full" />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  const covered = data.entries.filter((e) => e.covered);
  const missing = data.entries.filter((e) => !e.covered);
  const totalDocs = covered.reduce((s, e) => s + e.documents, 0);

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="md-headline-medium text-on-surface">التغطية القانونية</h1>
        <p className="md-body-large mt-2 max-w-2xl text-on-surface-variant">
          هذي كل القوانين اللي عدنا نصوصها — وكل اللي ما عدنا. ننشرها حتى تعرف بالضبط وين تكدر
          تعتمد علينا ووين لا. أداة قانونية ما تكولك شنو ناقصها، ما تنفع.
        </p>
      </div>

      {data.entries.length === 0 ? (
        <Banner tone="warn" icon="warning" title="ما عدنا أي نصوص مستوردة">
          قاعدة النصوص فارغة حالياً، فمجال «قانون» راح يرفض يجاوب بدل ما يخمّن. هذا تصرّف مقصود.
        </Banner>
      ) : null}

      {/* ─── Summary ─── */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card variant="filled" className="p-4">
          <p className="md-label-medium text-on-surface-variant">قوانين متوفرة</p>
          <p className="md-headline-small mt-1 text-on-surface">
            <bdi>{totalDocs}</bdi>
          </p>
        </Card>
        <Card variant="filled" className="p-4">
          <p className="md-label-medium text-on-surface-variant">مجالات مغطّاة</p>
          <p className="md-headline-small mt-1 text-on-surface">
            <bdi>
              {covered.length} / {data.entries.length}
            </bdi>
          </p>
        </Card>
        <Card variant="filled" className="p-4">
          <p className="md-label-medium text-on-surface-variant">آخر تحديث</p>
          <p className="md-title-medium mt-1 text-on-surface">
            <bdi>{data.updatedAt ?? "—"}</bdi>
          </p>
        </Card>
      </div>

      {/* ─── What we DON'T have — first, deliberately ─── */}
      {missing.length > 0 ? (
        <section>
          <h2 className="md-title-large mb-1 flex items-center gap-2 text-on-surface">
            <Icon name="do_not_disturb_on" size={22} className="text-error" filled />
            ما عدنا هذي — لا تسأل عنها
          </h2>
          <p className="md-body-medium mb-3 text-on-surface-variant">
            بهذي المجالات راح نرفض نجاوب بدل ما نخمّن. الرفض هنا هو الجواب الصحيح.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {missing.map((e) => (
              <MissingRow key={e.key} entry={e} />
            ))}
          </div>
        </section>
      ) : null}

      {/* ─── What we DO have ─── */}
      {covered.length > 0 ? (
        <section>
          <h2 className="md-title-large mb-3 flex items-center gap-2 text-on-surface">
            <Icon name="check_circle" size={22} className="text-success" filled />
            المتوفر عدنا
          </h2>
          <div className="grid gap-2">
            {covered.map((e) => (
              <CoveredRow key={e.key} entry={e} />
            ))}
          </div>
        </section>
      ) : null}

      {/* ─── Source ─── */}
      <Card variant="outlined" className="p-4">
        <h3 className="md-title-small text-on-surface">المصدر</h3>
        <p className="md-body-medium mt-1 text-on-surface-variant">
          النصوص مأخوذة من {data.source ?? "قاعدة التشريعات العراقية — مجلس القضاء الأعلى"}. نأخذ
          نص التشريع نفسه فقط، وهو من إصدارات الدولة، وما نأخذ الشروح والتعليقات التحريرية.
        </p>
        {data.sourceUrl ? (
          <a
            href={data.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="md-label-large mt-2 inline-flex items-center gap-1 text-primary hover:underline"
          >
            <Icon name="open_in_new" size={16} />
            <bdi>{data.sourceUrl}</bdi>
          </a>
        ) : null}
      </Card>

      <div className="flex flex-wrap gap-2">
        {/* A real anchor, styled as a tonal button — a <button> inside an <a>
            would be nested interactive content and invalid HTML. */}
        {corpusField ? (
          <Link
            to={{ name: "field", fieldId: corpusField.id }}
            navigate={navigate}
            className="md-state md-label-large inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-full bg-secondary-container px-6 text-on-secondary-container"
          >
            <Icon name={corpusField.icon} size={18} />
            روح لمجال {corpusField.title}
          </Link>
        ) : null}
        <Button variant="text" icon="refresh" onClick={() => void load()}>
          حدّث
        </Button>
      </div>
    </div>
  );
}

function MissingRow({ entry }: { entry: CoverageEntry }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-error/40 bg-error-container/25 p-3">
      <Icon name="cancel" size={20} className="mt-0.5 shrink-0 text-error" filled />
      <div className="min-w-0">
        <p className="md-title-small text-on-surface">{entry.label}</p>
        <p className="md-body-small mt-0.5 text-on-surface-variant">
          {entry.notes ?? "ما مستورد بعد — أي سؤال بهذا المجال راح ينرفض."}
        </p>
      </div>
    </div>
  );
}

function CoveredRow({ entry }: { entry: CoverageEntry }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-outline-variant bg-surface-lowest p-3">
      <Icon name="check_circle" size={20} className="shrink-0 text-success" filled />
      <p className="md-title-small min-w-0 flex-1 text-on-surface">{entry.label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="neutral" icon="description">
          <bdi>{entry.documents}</bdi> قانون
        </Chip>
        {typeof entry.articles === "number" ? (
          <Chip tone="neutral" icon="article">
            <bdi>{entry.articles}</bdi> مادة
          </Chip>
        ) : null}
        <Chip tone={entry.lastIngested ? "neutral" : "warn"} icon="schedule">
          {entry.lastIngested ? <bdi>{entry.lastIngested}</bdi> : "تاريخ غير معروف"}
        </Chip>
      </div>
    </div>
  );
}
